/**
 * 庫存顯示數量。後端只給 qty.baseQty 與單位 metadata，前端換算。
 * 只有 defaultDisplayUnit 為 CONVERT 且進位單位有效時才換算；其餘顯示基礎量。
 * 空進位投影（id、名稱皆空）不讀 convertUnit。
 * 小數後三位 HALF_UP，用整數運算，不用二進位浮點。
 */

function parseDecimal(value) {
  const text = String(value ?? '').trim();
  if (!/^-?\d+(\.\d+)?$/.test(text)) {
    return null;
  }
  const negative = text.startsWith('-');
  const unsigned = negative ? text.slice(1) : text;
  const [whole, frac = ''] = unsigned.split('.');
  const digits = `${whole}${frac}`.replace(/^0+(?=\d)/, '') || '0';
  return {
    negative,
    int: BigInt(digits),
    scale: frac.length,
  };
}

function formatScaled(intValue, scale) {
  const negative = intValue < 0n;
  const digits = (negative ? -intValue : intValue).toString().padStart(scale + 1, '0');
  const whole = digits.slice(0, digits.length - scale);
  const frac = digits.slice(digits.length - scale).replace(/0+$/, '');
  const body = frac ? `${whole}.${frac}` : whole;
  return negative ? `-${body}` : body;
}

/** 正數 HALF_UP 到指定小數位。分母須為正。 */
export function divideHalfUp(numerator, denominator, scale = 3) {
  const n = parseDecimal(numerator);
  const d = parseDecimal(denominator);
  if (!n || !d || d.int <= 0n || n.negative || scale < 0) {
    return null;
  }
  const num = n.int * (10n ** BigInt(d.scale + scale));
  const den = d.int * (10n ** BigInt(n.scale));
  let quotient = num / den;
  const remainder = num % den;
  if (remainder * 2n >= den) {
    quotient += 1n;
  }
  return formatScaled(quotient, scale);
}

export function readInventoryQty(row) {
  const qty = row?.qty;
  if (!qty || typeof qty !== 'object' || Array.isArray(qty)) {
    return null;
  }
  return qty;
}

function hasConvertUnit(convertUnit) {
  if (!convertUnit || typeof convertUnit !== 'object') {
    return false;
  }
  const hasId = convertUnit.id != null && String(convertUnit.id).trim() !== '';
  const hasName = convertUnit.name != null && String(convertUnit.name).trim() !== '';
  const rate = Number(convertUnit.convertRate);
  return (hasId || hasName) && Number.isFinite(rate) && rate > 0;
}

/**
 * @returns {{ text: string, unitName: string } | null}
 */
export function resolveInventoryDisplayQty(row) {
  const qty = readInventoryQty(row);
  if (!qty || qty.baseQty == null || qty.baseQty === '') {
    return null;
  }

  const convertUnit = hasConvertUnit(qty.convertUnit) ? qty.convertUnit : null;
  const preferConvert = qty.defaultDisplayUnit === 'CONVERT' && convertUnit;

  if (!preferConvert) {
    const text = divideHalfUp(qty.baseQty, 1, 3);
    return {
      text,
      unitName: qty.baseUnit?.name || '',
    };
  }

  const text = divideHalfUp(qty.baseQty, convertUnit.convertRate, 3);
  if (text == null) {
    return null;
  }
  return {
    text,
    unitName: convertUnit.name || '',
  };
}

export function formatInventoryDisplayQty(row) {
  const display = resolveInventoryDisplayQty(row);
  if (!display || display.text == null) {
    return '-';
  }
  return display.unitName ? `${display.text} ${display.unitName}` : display.text;
}
